import { useEffect, useState } from 'react';
import { Sparkles, Shield, Gift, ShoppingBag } from 'lucide-react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { Toggle } from '../ui/Toggle';
import { axiosInstanceN8N } from '../../../services/instance';
import Loader from '../../common/Loader';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { VisualStyle } from '../../../types/onboarding';

export function StepBrandAndPrice() {
  const { data, updateVisualStyle, updateProductData, updateAdditionalOffers } = useOnboardingStore();
  const { visualStyle, productData } = data;
  const { language } = useAuth();
  const [brandName, setBrandName] = useState(visualStyle.brandName || '');
  const [mainPrice, setMainPrice] = useState(productData.price || '');
  const [bumpOfferPrice, setBumpOfferPrice] = useState(data.additionalOffers.bumpOffer?.price || '');
  const [warrantyEnabled, setWarrantyEnabled] = useState(productData.warranty !== 'none');
  const [isGenerating, setIsGenerating] = useState(false);
  const [brandNameSuggestions, setBrandNameSuggestions] = useState<string[]>([]);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);

  const fetchSuggestedBrandNames = async () => {
    try {
      const response = await axiosInstanceN8N.post('/webhook/suggested-brand-names', {
        product_name: (data.productData?.name || "").split(' ').slice(0, 5).join(' '),
        product_description: (data.productData?.details || "").split(' ').slice(0, 5).join(' ')

      });

      if (response.data.data && response.data.data.brand_names.length > 0) {
        return response.data.data.brand_names;
      }

      return ["Brandify", "BrandMaster", "BrandWizard", "BrandNova", "BrandPeak"];
    } catch (error) {
      console.error("Error fetching brand names:", error);
      return ["Brandify", "BrandMaster", "BrandWizard", "BrandNova", "BrandPeak"];
    }
  };


  const loadBrandNames = async () => {
    setIsGenerating(true);
  
    const suggestions = await fetchSuggestedBrandNames();
    setBrandNameSuggestions(suggestions);
  
    const updates: Partial<VisualStyle> = {};
  
    if (suggestions.length > 0) {
      updates.brandName = suggestions[0];
      setBrandName(suggestions[0]);
    }
  
    if (visualStyle.colorPalette === 'custom') {
      updates.colorPalette = 'professional-blue';
    }
  
    if (Object.keys(updates).length > 0) {
      updateVisualStyle(updates);
    }
  
    setIsGenerating(false);
  };

  const handleGenerateBrandName = () => {
    if (isGenerating || brandNameSuggestions.length === 0) {
      return loadBrandNames();
    }

    setIsGenerating(true);

    try {
      const nextIndex = (currentSuggestionIndex + 1) % brandNameSuggestions.length;
      const newName = brandNameSuggestions[nextIndex];

      setBrandName(newName);
      updateVisualStyle({
        brandName: newName
      });

      setCurrentSuggestionIndex(nextIndex);
    } catch (error) {
      console.error("Error cycling brand name:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBrandName(value);
    updateVisualStyle({ brandName: value });
  };

  const handleMainPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMainPrice(value);
    updateProductData({ ...productData, price: value });
  };

  const handleBumpOfferPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBumpOfferPrice(value);

    if (data.additionalOffers.bumpOffer) {
      updateAdditionalOffers({
        ...data.additionalOffers,
        bumpOffer: {
          ...data.additionalOffers.bumpOffer,
          price: value
        }
      });
    }
  };

  useEffect(() => {
    setWarrantyEnabled(true);
    updateProductData({
      ...productData,
      warranty: '30days',
      customWarrantyDays: '30'
    });
  }, []);

  const handleWarrantyToggle = (enabled: boolean) => {
    setWarrantyEnabled(enabled);
    updateProductData({
      ...productData,
      warranty: enabled ? '30days' : 'none',
      customWarrantyDays: enabled ? '30' : undefined
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {dictionary["only-a-few-steps-left"][language]}
        </h1>
      </div>

      <div className="max-w-2xl mx-auto space-y-3">
        <div>
          <label className="block font-extrabold text-sm text-[#1E293B] mb-1">
            {dictionary["brand-name"][language]}
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={brandName}
              disabled={isGenerating}
              onChange={handleBrandNameChange}
              placeholder={dictionary["enter-your-brand-name-placeholder"][language]}
              className="rounded-[16px] text-xs flex-1 px-4 py-2 border border-[#E2E8F0] focus:border-[#3B4A9F] focus:outline-none focus:ring-1 focus:ring-[#3B4A9F]"
            />
            <button
              onClick={handleGenerateBrandName}
              className="rounded-[16px] px-6 py-2 bg-[#F8FAFC] border text-[#6366F1] hover:bg-gray-100 font-medium transition-colors flex items-center gap-2"
            >
              {isGenerating ? <Loader size={20} /> : <Sparkles className="w-5 h-5" />}
              {dictionary["generate-with-ai"][language]}
            </button>
          </div>
        </div>

        <div className="bg-[#FCFCFD] rounded-[18px] border border-[#E2E8F0] p-2">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
              {data.visualContent.imagesBase64[0] || data.visualContent.images[0] ? (
                <img src={data.visualContent.imagesBase64[0] || data.visualContent.images[0]} alt={productData.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {productData.name || dictionary["main-product"][language]}
              </h3>
              <span className="inline-block mt-1 px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] text-xs font-medium rounded">
                {dictionary["main-product"][language]}
              </span>
            </div>
            <div className="relative w-32">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                value={mainPrice}
                onChange={handleMainPriceChange}
                placeholder="50000"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:border-[#3B4A9F] focus:outline-none focus:ring-1 focus:ring-[#3B4A9F] text-right"
              />
            </div>
          </div>
        </div>

        {data.additionalOffers.bumpOffer?.enabled && data.additionalOffers.bumpOffer?.productName && (
          <div className="bg-[#F6FFFA] rounded-[18px] border border-[#E3FBEB] p-2">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                {data.additionalOffers.bumpOffer.url_images || data.additionalOffers.bumpOffer.image ? (
                  <img src={data.additionalOffers.bumpOffer.url_images || data.additionalOffers.bumpOffer.image} alt={data.additionalOffers.bumpOffer.productName} className="w-full h-full object-cover border-[1px] border-[#E3FBEB] rounded-xl" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-[#15803D] truncate">
                  {data.additionalOffers.bumpOffer.productName || dictionary["order-bump"][language]}
                </h3>
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-[#E3FBEB] text-[#15803D] text-xs font-medium rounded">
                  <Gift className="w-4 h-4 text-green-800" />
                  {dictionary["order-bump"][language]}
                </span>
              </div>
              <div className="relative w-32">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={bumpOfferPrice}
                  onChange={handleBumpOfferPriceChange}
                  placeholder="12000"
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:border-[#3B4A9F] focus:outline-none focus:ring-1 focus:ring-[#3B4A9F] text-right"
                />
              </div>
            </div>
          </div>
        )}

        {data.additionalOffers.upsell?.enabled && data.additionalOffers.upsell.products.map((upsell) => (
          <div key={upsell.id} className="bg-[#FBFAFF] rounded-[18px] border border-[#EFECFF] p-2">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                {upsell.url_images?.[0] || upsell.image ? (
                  <img src={upsell.url_images?.[0] || upsell.image || ''} alt={upsell.productName} className="w-full h-full object-cover border-[1px] border-[#EFECFF] rounded-xl" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-100 to-purple-200" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-[#5B20B6] truncate">
                  {upsell.productName || `${dictionary["upsell-title"][language]}`}
                </h3>
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-[#EDE9FE] text-[#5B20B6] text-xs font-medium rounded">
                  <ShoppingBag className="w-4 h-4 text-[#6D29D9]" />
                  {dictionary["upsell-title"][language]}
                </span>
              </div>
              <div className="relative w-32">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={upsell.price}
                  onChange={(e) => {
                    const updatedProducts = data.additionalOffers.upsell.products.map(p =>
                      p.id === upsell.id ? { ...p, price: e.target.value } : p
                    );
                    updateAdditionalOffers({
                      ...data.additionalOffers,
                      upsell: {
                        ...data.additionalOffers.upsell,
                        products: updatedProducts
                      }
                    });
                  }}
                  placeholder="25000"
                  className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:border-[#3B4A9F] focus:outline-none focus:ring-1 focus:ring-[#3B4A9F] text-right"
                />
              </div>
            </div>
          </div>
        ))}

        <hr className="border-[#E2E8F0 opacity-50" style={{ margin: '30px 8px' }} />

        <div
          className={`rounded-[16px] px-4 py-3 mt-6 border flex justify-between items-center transition-all duration-200
  ${warrantyEnabled ? "bg-[#F6FFFA] border-[#A3F3C4]" : "bg-[#FCFCFD] border-gray-200"}`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center shadow-[0_0_10px_rgba(0,0,0,0.15)] 
      ${warrantyEnabled ? "bg-[#D9F7E8]" : "bg-white"}`}
            >
              <Shield className={`w-5 h-5 ${warrantyEnabled ? "text-green-700" : ""}`} />
            </div>

            <span className="font-medium text-gray-900">
              {dictionary["warranty"][language]}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {warrantyEnabled && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">{dictionary["days"][language]}:</span>
                <input
                  type="number"
                  value={productData.customWarrantyDays}
                  onChange={(e) => {
                    updateProductData({
                      ...productData,
                      customWarrantyDays: e.target.value
                    });
                  }}
                  className="w-20 px-3 py-1.5 rounded-lg border border-[#A3F3C4] bg-white text-right text-sm focus:outline-none focus:border-green-500"
                />
              </div>
            )}

            <Toggle enabled={warrantyEnabled} onChange={handleWarrantyToggle} />
          </div>
        </div>

      </div>
    </div>
  );
}
